
import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Phone, MessageCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

interface ContactOptionsDialogProps {
  phone?: string | null;
  phoneCountryCode?: string | null;
  whatsappEnabled?: boolean;
  viberEnabled?: boolean;
  onInternalChat?: () => void;
  showInternalChat?: boolean;
  children: React.ReactNode;
}

const ContactOptionsDialog: React.FC<ContactOptionsDialogProps> = ({
  phone,
  phoneCountryCode = '+355',
  whatsappEnabled = false,
  viberEnabled = false,
  onInternalChat,
  showInternalChat = false,
  children
}) => {
  const { isAuthenticated } = useAuth();
  const fullPhoneNumber = phone ? `${phoneCountryCode}${phone}` : null;
  const cleanPhoneNumber = phone ? phone.replace(/\D/g, '') : null;
  
  const handleWhatsApp = () => {
    if (cleanPhoneNumber) {
      const countryCodeNumber = phoneCountryCode?.replace('+', '') || '355';
      window.open(`https://wa.me/${countryCodeNumber}${cleanPhoneNumber}`, '_blank');
    }
  };

  const handleViber = () => {
    if (fullPhoneNumber) {
      window.open(`viber://contact?number=${fullPhoneNumber}`, '_blank');
    }
  };

  const handlePhone = () => {
    if (fullPhoneNumber) {
      window.open(`tel:${fullPhoneNumber}`, '_blank');
    }
  };

  const hasContactOptions = phone || (showInternalChat && isAuthenticated);

  if (!hasContactOptions) {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Kontakto shitësin</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Internal Chat Option - First for authenticated users */}
          {showInternalChat && isAuthenticated && onInternalChat && (
            <Button
              variant="default"
              className="w-full flex items-center justify-start gap-3 bg-brand-500 hover:bg-brand-600 text-white"
              onClick={onInternalChat}
            >
              <MessageCircle className="h-4 w-4" />
              <span>Dërgo Mesazh (Intern)</span>
            </Button>
          )}

          {phone && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-start gap-3"
              onClick={handlePhone}
            >
              <Phone className="h-4 w-4" />
              <span>Telefono: {fullPhoneNumber}</span>
            </Button>
          )}

          {phone && whatsappEnabled && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-start gap-3 text-green-600 border-green-200 hover:bg-green-50"
              onClick={handleWhatsApp}
            >
              <MessageCircle className="h-4 w-4" />
              <span>WhatsApp</span>
            </Button>
          )}

          {phone && viberEnabled && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-start gap-3 text-purple-600 border-purple-200 hover:bg-purple-50"
              onClick={handleViber}
            >
              <MessageCircle className="h-4 w-4" />
              <span>Viber</span>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactOptionsDialog;
